// src/utils/componentMappings.js
import SelectInput from '../components/FormElements/SelectInput.jsx';
import TextInput from '../components/FormElements/TextInput.jsx';
import RadioInput from '../components/FormElements/RadioInput.jsx';
import SelectableOption from '../components/FormElements/SelectableOption.jsx';
import SelectableImageOption from '../components/FormElements/SelectableImageOption.jsx';

import NumberInputWithToggle from '../components/DynamicInputs/NumberInputWithToggle.jsx';
import SelectableOptionWithOther from '../components/DynamicInputs/SelectableOptionWithOther.jsx';
import MultiSectionSelectableOption from '../components/DynamicInputs/MultiSectionSelectableOption.jsx';
import CheckboxGroupWithOther from '../components/DynamicInputs/CheckboxGroupWithOther.jsx'; // Assuming you extracted this
import DateRangePicker from '../components/DynamicInputs/DateRangePicker.jsx';
import MultiSectionCheckboxGroup from '../components/DynamicInputs/MultiSectionCheckboxGroup.jsx'; // Assuming you extracted this
import MultiSelectWithOther from '../components/DynamicInputs/multiSelectWithOther.jsx';


// You might want to create a validation utility for these handleChange functions as well.
export const componentMap = {
    "multiSelect": (props) => {
        const { onChange: driverOnChange, schema, value: currentValue, ...rest } = props;
        const { validation, errorMessages, componentProps } = schema;

        const handleChange = (event) => {
            const newValue = event.target.value;
            let error = '';

            if (validation?.required && (!newValue || newValue.length === 0)) {
                error = errorMessages?.required || "Selection is required.";
            } else if (validation?.minSelections && newValue && newValue.length < validation.minSelections) {
                error = errorMessages?.minSelections || `Please select at least ${validation.minSelections} option(s).`;
            }
            if (!error && validation?.maxSelections && newValue && newValue.length > validation.maxSelections) {
                error = errorMessages?.maxSelections || `Please select no more than ${validation.maxSelections} option(s).`;
            }
            driverOnChange(newValue, error);
        };
        return (
            <SelectInput
                {...rest}
                {...componentProps}
                value={currentValue}
                onChange={handleChange}
                multiple={true}
            />
        );
    },
    "singleSelect": (props) => {
        const { onChange: driverOnChange, schema, value: currentValue, ...rest } = props;
        const { validation, errorMessages, componentProps } = schema;

        const handleChange = (event) => {
            const newValue = event.target.value;
            let error = '';
            if (validation?.required && !newValue) {
                error = errorMessages?.required || "Selection is required.";
            }
            driverOnChange(newValue, error);
        };
        return <SelectInput {...rest} {...componentProps} value={currentValue} onChange={handleChange} multiple={false} />;
    },
    "textInput": (props) => {
        const { onChange: driverOnChange, schema, value: currentValue, ...rest } = props;
        const { validation, errorMessages, componentProps } = schema;

        const handleChange = (event) => {
            const newValue = event.target.value;
            let error = '';
            if (validation?.required && newValue.trim() === '') {
                error = errorMessages?.required || "This field is required.";
            }
            if (!error && validation?.minLength && newValue.length < validation.minLength) {
                error = errorMessages?.minLength || `Minimum length is ${validation.minLength} characters.`;
            }
            if (!error && validation?.maxLength && newValue.length > validation.maxLength) {
                error = errorMessages?.maxLength || `Maximum length is ${validation.maxLength} characters.`;
            }
            if (!error && validation?.pattern && !new RegExp(validation.pattern).test(newValue)) {
                error = errorMessages?.pattern || "Invalid format.";
            }
            driverOnChange(newValue, error);
        };
        return <TextInput {...rest} {...componentProps} value={currentValue} onChange={handleChange} />;
    },
    "hiddenInput": ({ schema }) => {
        const defaultValue = schema.defaultValue !== undefined ? schema.defaultValue : 1;
        return <input type="hidden" name={schema.id} defaultValue={defaultValue} readOnly />;
    },
    "numberInput": (props) => {
        const { onChange: driverOnChange, schema, value: currentRawValue, ...rest } = props;
        const { validation, errorMessages, componentProps } = schema;

        const handleInputChange = (event) => {
            const rawValue = event.target.value;
            let error = '';

            if (rawValue.trim() === '') {
                if (validation?.required) {
                    error = errorMessages?.required || "This field is required.";
                }
                driverOnChange(rawValue, error);
                return;
            }

            const numValue = validation?.integer ? parseInt(rawValue, 10) : parseFloat(rawValue);

            if (isNaN(numValue)) {
                error = errorMessages?.numeric || "Please enter a valid number.";
            } else {
                if (validation?.min !== undefined && numValue < validation.min) {
                    error = errorMessages?.min || `Value must be at least ${validation.min}.`;
                }
                if (!error && validation?.max !== undefined && numValue > validation.max) {
                    error = errorMessages?.max || `Value must not exceed ${validation.max}.`;
                }
                if (!error && validation?.integer && !/^-?\d+$/.test(rawValue)) {
                    error = errorMessages?.integer || "Please enter a whole number.";
                }
            }
            driverOnChange(rawValue, error);
        };
        return <TextInput {...rest} {...componentProps} type="number" value={currentRawValue === undefined || currentRawValue === null ? '' : String(currentRawValue)} onChange={handleInputChange} />;
    },
    "textArea": (props) => {
        const { onChange: driverOnChange, schema, value: currentValue, ...rest } = props;
        const { validation, errorMessages, componentProps } = schema;

        const handleChange = (event) => {
            const newValue = event.target.value;
            let error = '';
            if (validation?.required && newValue.trim() === '') {
                error = errorMessages?.required || "This field is required.";
            }
            if (!error && validation?.minLength && newValue.length < validation.minLength) {
                error = errorMessages?.minLength || `Minimum length is ${validation.minLength} characters.`;
            }
            if (!error && validation?.maxLength && newValue.length > validation.maxLength) {
                error = errorMessages?.maxLength || `Maximum length is ${validation.maxLength} characters.`;
            }
            driverOnChange(newValue, error);
        };
        return <TextInput {...rest} {...componentProps} type="textarea" value={currentValue} onChange={handleChange} />;
    },
    "radioGroup": ({ schema, value: currentValue, onChange: driverOnChange }) => {
        const { options, id: questionId, layout, errorMessages, validation, componentProps = {} } = schema;
        const answerLayout = layout?.answerLayout || {};

        let containerClassNames = "question-options-container";
        const containerStyles = {};

        if (answerLayout.type === 'flex') {
            containerClassNames += ' d-flex';
            if (answerLayout.direction) {
                containerClassNames += ` flex-${answerLayout.direction}`;
            }
            if (answerLayout.mdDirection) {
                containerClassNames += ` flex-md-${answerLayout.mdDirection}`;
            }
            if (answerLayout.justifyContent) {
                containerClassNames += ` justify-content-${answerLayout.justifyContent}`;
            }
            if (answerLayout.alignItems) {
                containerClassNames += ` align-items-${answerLayout.alignItems}`;
            }
            if (answerLayout.wrap) {
                containerClassNames += ` flex-${answerLayout.wrap}`;
            }
            if (answerLayout.gap && /^\d+$/.test(answerLayout.gap)) {
                containerClassNames += ` gap-${answerLayout.gap}`;
            } else if (answerLayout.gap) {
                containerStyles.gap = answerLayout.gap;
            }
        }
        if (layout?.answerOptionsContainerClassName) {
            containerClassNames += ` ${layout.answerOptionsContainerClassName}`;
        }

        const handleRadioChange = (event) => {
            const newValue = event.target.value;
            let error = '';
            if (validation?.required && !newValue) {
                error = errorMessages?.required || "Selection required";
            }
            driverOnChange(newValue, error);
        };

        return (
            <div className={containerClassNames} style={containerStyles}>
                {options.map(opt => {
                    const labelText = opt.mainText ? (
                        <>
                            {opt.mainText}
                            {opt.subText && <small className="text-muted d-block mt-1">{opt.subText}</small>}
                        </>
                    ) : (opt.label || opt.text);

                    return (
                        <RadioInput
                            key={opt.id || opt.value}
                            name={questionId}
                            id={opt.id || `${questionId}-${opt.value}`}
                            value={opt.value}
                            checked={currentValue === opt.value}
                            onChange={handleRadioChange}
                            labelText={labelText}
                            labelClassName={componentProps?.labelClassName}
                        />
                    );
                })}
            </div>
        );
    },
    "checkboxGroup": ({ schema, value: currentValue, onChange: driverOnChange }) => {
        const { validation, errorMessages, options } = schema;

        const handleChange = (optionValue, isChecked) => {
            const currentSelectedArray = Array.isArray(currentValue) ? currentValue : [];
            let newValue;
            if (isChecked) {
                newValue = [...currentSelectedArray, optionValue];
            } else {
                newValue = currentSelectedArray.filter(v => v !== optionValue);
            }

            let error = '';
            if (validation?.required && newValue.length === 0) {
                error = errorMessages?.required || "Selection required";
            } else if (validation?.minSelections && newValue.length < validation.minSelections) {
                error = errorMessages?.minSelections || `Select at least ${validation.minSelections} option(s).`;
            }
            if (!error && validation?.maxSelections && newValue.length > validation.maxSelections) {
                error = errorMessages?.maxSelections || `Select no more than ${validation.maxSelections} option(s).`;
            }
            driverOnChange(newValue, error);
        };

        return (
            <div>
                {options.map(opt => (
                    <label key={opt.id || opt.value} style={{ marginRight: '10px' }}>
                        <input type="checkbox" value={opt.value} checked={Array.isArray(currentValue) && currentValue.includes(opt.value)} onChange={(e) => handleChange(opt.value, e.target.checked)} />
                        {opt.label || opt.text}
                    </label>
                ))}
            </div>
        );
    },
    "selectableOption": ({ schema, value: currentValue, onChange: driverOnChange }) => {
        const isMultiSelect = schema.componentProps?.isMultiSelect;
        const { options = [], validation, errorMessages, componentProps, layout } = schema;

        const handleOptionClick = (clickedOptionValue) => {
            let newValue;
            if (isMultiSelect) {
                const currentSelectedArray = Array.isArray(currentValue) ? currentValue : [];
                if (currentSelectedArray.includes(clickedOptionValue)) {
                    newValue = currentSelectedArray.filter(v => v !== clickedOptionValue);
                } else {
                    newValue = [...currentSelectedArray, clickedOptionValue];
                }
            } else {
                newValue = clickedOptionValue === currentValue ? '' : clickedOptionValue;
            }

            let error = '';
            if (validation?.required) {
                if (isMultiSelect && newValue.length === 0) {
                    error = errorMessages?.required || "Selection required";
                } else if (!isMultiSelect && (newValue === undefined || newValue === '')) {
                    error = errorMessages?.required || "Selection required";
                }
            }
            if (!error && validation?.minSelections && isMultiSelect && newValue.length < validation.minSelections) {
                error = errorMessages?.minSelections || errorMessages?.min || `Select at least ${validation.minSelections} option(s).`;
            }
            if (!error && validation?.maxSelections && isMultiSelect && newValue.length > validation.maxSelections) {
                error = errorMessages?.maxSelections || `Select no more than ${validation.maxSelections} option(s).`;
            }

            driverOnChange(newValue, error);
        };
        const answerLayout = layout?.answerLayout || {};
        let containerStyle = {};
        if (answerLayout.type === 'flex') {
            containerStyle = {
                display: 'flex',
                flexDirection: answerLayout.direction || 'row',
                flexWrap: answerLayout.wrap || 'wrap',
                gap: answerLayout.gap
                    ? (/^\d+$/.test(answerLayout.gap)
                        ? `${parseFloat(answerLayout.gap) * 0.25}rem`
                        : answerLayout.gap)
                    : '0.5rem',
                justifyContent: answerLayout.justifyContent || 'flex-start',
                alignItems: answerLayout.alignItems || 'stretch',
            };
        } else if (answerLayout.type === 'grid') {
            const columns = answerLayout.columnsPerSection || 2;
            let gapValue = '0.5rem';
            if (answerLayout.gap) {
                if (typeof answerLayout.gap === 'string' && answerLayout.gap.includes(' ')) {
                    // Support two values for row/column gap, e.g., "1 3"
                    const [rowGap, colGap] = answerLayout.gap.split(' ').map(v => {
                        if (/^\d+$/.test(v)) return `${parseFloat(v) * 0.25}rem`;
                        return v;
                    });
                    gapValue = `${rowGap} ${colGap}`;
                } else if (/^\d+$/.test(answerLayout.gap)) {
                    gapValue = `${parseFloat(answerLayout.gap) * 0.25}rem`;
                } else if (typeof answerLayout.gap === 'string') {
                    gapValue = answerLayout.gap;
                }
            }
            containerStyle = {
                display: 'grid',
                gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
                gap: gapValue,
                alignItems: answerLayout.alignItems || 'stretch',
                justifyItems: answerLayout.justifyItems || 'stretch',
            };
        }

        return (
            <div style={containerStyle} className={layout?.answerOptionsContainerClassName}>
                {options.map(opt => (
                    <SelectableOption
                        key={opt.value}
                        option={opt}
                        isSelected={
                            isMultiSelect
                                ? Array.isArray(currentValue) && currentValue.includes(opt.value)
                                : currentValue === opt.value
                        }
                        onClick={() => handleOptionClick(opt.value)}
                        className={componentProps?.optionClassName}
                    />
                ))}
            </div>
        );
    },
    "selectableImageOption": ({ schema, value: currentValue, onChange: driverOnChange }) => {
        const { options = [], validation, errorMessages, componentProps, layout } = schema;
        const isMultiSelect = componentProps?.isMultiSelect;

        const handleOptionClick = (clickedOptionValue) => {
            let newValue;
            if (isMultiSelect) {
                const currentSelectedArray = Array.isArray(currentValue) ? currentValue : [];
                newValue = currentSelectedArray.includes(clickedOptionValue)
                    ? currentSelectedArray.filter(v => v !== clickedOptionValue)
                    : [...currentSelectedArray, clickedOptionValue];
            } else {
                newValue = clickedOptionValue === currentValue ? '' : clickedOptionValue;
            }
            let error = '';
            if (validation?.required && ((isMultiSelect && newValue.length === 0) || (!isMultiSelect && !newValue))) {
                error = errorMessages?.required || "Selection required";
            }
            driverOnChange(newValue, error);
        };

        const answerLayout = layout?.answerLayout || {};
        const containerStyle = answerLayout.type === 'flex' ? { display: 'flex', flexDirection: answerLayout.direction || 'row', flexWrap: answerLayout.wrap || 'wrap', gap: answerLayout.gap ? (/^\d+$/.test(answerLayout.gap) ? `${parseFloat(answerLayout.gap) * 0.25}rem` : answerLayout.gap) : '0.5rem', justifyContent: answerLayout.justifyContent || 'flex-start', alignItems: answerLayout.alignItems || 'stretch' } : {};

        return (
            <div style={containerStyle} className={layout?.answerOptionsContainerClassName}>
                {options.map(opt => (
                    <SelectableImageOption
                        key={opt.value}
                        option={opt}
                        isSelected={isMultiSelect ? (Array.isArray(currentValue) && currentValue.includes(opt.value)) : currentValue === opt.value}
                        onClick={() => handleOptionClick(opt.value)}
                        className={componentProps?.optionClassName}
                        imageClassName={componentProps?.imageClassName}
                        textClassName={componentProps?.textClassName}
                    />
                ))}
            </div>
        );
    },
    "dateRangePicker": DateRangePicker,
    "checkboxGroupWithOther": CheckboxGroupWithOther, // Use extracted component
    "multiSectionCheckboxGroup": MultiSectionCheckboxGroup, // Use extracted component
    "numberInputWithToggle": NumberInputWithToggle, // Use extracted component
    "selectableOptionWithOther": SelectableOptionWithOther, // Use extracted component
    "multiSectionSelectableOption": MultiSectionSelectableOption, // Use extracted component
    "multiSelectWithOther": MultiSelectWithOther,
    "staticContent": ({ schema }) => <div>{schema.title}{schema.subtitle && <p>{schema.subtitle}</p>}</div>,
};